/**
 * AGROMX Frontend - Personas List View
 * Vista de catálogo de personas (clientes, proveedores, responsables)
 */

import personaService from '../../services/persona.service.js';
import permisos from '../../services/permissions.service.js';
import router from '../../router/router.js';
import modal from '../../utils/modal.js';

export default {
  data: {
    personas: [],
    loading: true,
    filters: {
      search: '',
      rol: '',
      tipo_persona: '',
      activo: ''
    },
    pagination: {
      page: 1,
      limit: 15,
      total: 0,
      totalPages: 0
    },
    // Rol preseleccionado desde la URL
    rolFiltrado: null
  },

  async render() {
    // Reiniciar estado al entrar a la vista
    this.data.personas = [];
    this.data.loading = true;
    this.data.filters = {
      search: '',
      rol: '',
      tipo_persona: '',
      activo: ''
    };
    this.data.pagination = {
      page: 1,
      limit: 15,
      total: 0,
      totalPages: 0
    };

    // Obtener rol desde parámetros de URL si existe (usando search, no hash)
    const urlParams = new URLSearchParams(window.location.search);
    this.data.rolFiltrado = urlParams.get('rol');

    if (this.data.rolFiltrado) {
      this.data.filters.rol = this.data.rolFiltrado;
    }

    const roles = personaService.getRoles();
    const rolesOptions = roles.map(r =>
      `<option value="${r.value}" ${this.data.filters.rol === r.value ? 'selected' : ''}>${r.label}</option>`
    ).join('');

    const tiposPersona = personaService.getTiposPersona();
    const tiposOptions = tiposPersona.map(t =>
      `<option value="${t.value}">${t.label}</option>`
    ).join('');

    const titulo = this.getTituloSegunRol();
    const moduloKey = this._getModuloKey();

    return `
      <div class="page-header">
        <h1>${titulo}</h1>
        ${permisos.canCreate(moduloKey) ? `<button class="btn btn-primary" id="btnNuevaPersona">
          + Nueva Persona
        </button>` : ''}
      </div>

      <!-- Estadísticas -->
      <div class="stats-grid" id="statsContainer">
        <div class="stat-card">
          <div class="stat-icon">👥</div>
          <div class="stat-content">
            <div class="stat-value" id="statTotal">-</div>
            <div class="stat-label">Total Registros</div>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon">🏢</div>
          <div class="stat-content">
            <div class="stat-value" id="statMorales">-</div>
            <div class="stat-label">Personas Morales</div>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon">👤</div>
          <div class="stat-content">
            <div class="stat-value" id="statFisicas">-</div>
            <div class="stat-label">Personas Físicas</div>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon">✅</div>
          <div class="stat-content">
            <div class="stat-value" id="statActivos">-</div>
            <div class="stat-label">Activos</div>
          </div>
        </div>
      </div>

      <!-- Filtros -->
      <div class="filters-card">
        <div class="filters-grid">
          <div class="form-group">
            <label class="form-label">Buscar</label>
            <input
              type="text"
              id="searchInput"
              class="form-control"
              placeholder="Nombre, RFC, código, email..."
            />
          </div>
          <div class="form-group">
            <label class="form-label">Tipo de Rol</label>
            <select id="rolFilter" class="form-control" ${this.data.rolFiltrado ? 'disabled' : ''}>
              <option value="">Todos los roles</option>
              ${rolesOptions}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Tipo de Persona</label>
            <select id="tipoPersonaFilter" class="form-control">
              <option value="">Todos</option>
              ${tiposOptions}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Estado</label>
            <select id="activoFilter" class="form-control">
              <option value="">Todos</option>
              <option value="1">Activos</option>
              <option value="0">Inactivos</option>
            </select>
          </div>
          <div class="form-group" style="display: flex; align-items: flex-end;">
            <button class="btn btn-secondary" id="btnLimpiarFiltros">Limpiar</button>
          </div>
        </div>
      </div>

      <!-- Tabla de personas -->
      <div class="card">
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th></th>
                <th class="tbl-hide-mobile">Código</th>
                <th>Nombre</th>
                <th class="tbl-hide-mobile">RFC</th>
                <th class="tbl-hide-mobile">Tipo</th>
                <th class="tbl-hide-mobile">Roles</th>
                <th class="tbl-hide-mobile">Contacto</th>
                <th class="tbl-hide-mobile">Estado</th>
                <th class="tbl-hide-mobile">Acciones</th>
              </tr>
            </thead>
            <tbody id="personasTableBody">
              <tr>
                <td colspan="9" class="text-center">
                  <div class="loading-spinner"></div>
                  Cargando datos...
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div class="pagination-container" id="paginationContainer"></div>
      </div>
    `;
  },

  getTituloSegunRol() {
    const titulos = {
      cliente: 'Catálogo de Clientes',
      proveedor: 'Catálogo de Proveedores',
      responsable: 'Catálogo de Responsables',
      distribuidor: 'Catálogo de Distribuidores',
      agente_aduanal: 'Catálogo de Agentes Aduanales'
    };
    return titulos[this.data.rolFiltrado] || 'Catálogo de Personas';
  },

  // Mapea el rol filtrado a la clave del módulo en el sistema de permisos
  _getModuloKey() {
    const map = {
      cliente: 'clientes',
      responsable: 'responsables',
      distribuidor: 'distribuidores',
      agente_aduanal: 'agentes_aduanales'
    };
    return map[this.data.rolFiltrado] || 'personas';
  },

  async afterRender() {
    const moduloKey = this._getModuloKey();
    if (!permisos.canView(moduloKey)) {
      if (permisos.primeraRuta) { router.navigate(permisos.primeraRuta); }
      else { document.getElementById('content').innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:60vh;"><div style="text-align:center;color:var(--primary);"><h2>Acceso denegado</h2><p>No tienes permisos para acceder a este módulo.</p></div></div>'; }
      return;
    }
    window.personasView = this;
    await this.cargarPersonas();
    this.setupEventListeners();
  },

  setupEventListeners() {
    // Botón nueva persona
    document.getElementById('btnNuevaPersona')?.addEventListener('click', () => {
      const rolParam = this.data.rolFiltrado ? `?rol=${this.data.rolFiltrado}` : '';
      router.navigate(`/catalogos/personas/nueva${rolParam}`);
    });

    // Filtros con debounce para búsqueda
    let searchTimeout;
    document.getElementById('searchInput')?.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.data.filters.search = e.target.value;
        this.data.pagination.page = 1;
        this.cargarPersonas();
      }, 500);
    });

    document.getElementById('rolFilter')?.addEventListener('change', (e) => {
      this.data.filters.rol = e.target.value;
      this.data.pagination.page = 1;
      this.cargarPersonas();
    });

    document.getElementById('tipoPersonaFilter')?.addEventListener('change', (e) => {
      this.data.filters.tipo_persona = e.target.value;
      this.data.pagination.page = 1;
      this.cargarPersonas();
    });

    document.getElementById('activoFilter')?.addEventListener('change', (e) => {
      this.data.filters.activo = e.target.value;
      this.data.pagination.page = 1;
      this.cargarPersonas();
    });

    document.getElementById('btnLimpiarFiltros')?.addEventListener('click', () => {
      this.limpiarFiltros();
    });
  },

  async cargarPersonas() {
    try {
      this.data.loading = true;
      const tbody = document.getElementById('personasTableBody');
      tbody.innerHTML = `
        <tr>
          <td colspan="9" class="text-center">
            <div class="loading-spinner"></div>
            Cargando datos...
          </td>
        </tr>
      `;

      const params = {
        page: this.data.pagination.page,
        limit: this.data.pagination.limit
      };

      // Agregar filtros no vacíos
      if (this.data.filters.search) params.search = this.data.filters.search;
      if (this.data.filters.rol) params.rol = this.data.filters.rol;
      if (this.data.filters.tipo_persona) params.tipo_persona = this.data.filters.tipo_persona;
      if (this.data.filters.activo !== '') params.activo = this.data.filters.activo;

      const response = await personaService.list(params);

      this.data.personas = response.data?.personas || response.personas || [];
      this.data.pagination = response.data?.pagination || response.pagination || this.data.pagination;

      this.renderTabla();
      this.renderPaginacion();
      this.actualizarEstadisticas();
    } catch (error) {
      console.error('Error al cargar personas:', error);
      const tbody = document.getElementById('personasTableBody');
      tbody.innerHTML = `
        <tr>
          <td colspan="9" class="text-center text-danger">
            Error al cargar datos: ${error.message || 'Error desconocido'}
          </td>
        </tr>
      `;
    } finally {
      this.data.loading = false;
    }
  },

  actualizarEstadisticas() {
    const personas = this.data.personas;
    const total = this.data.pagination.total || personas.length;
    const morales = personas.filter(p => p.tipo_persona === 'moral').length;
    const fisicas = personas.filter(p => p.tipo_persona === 'fisica').length;
    const activos = personas.filter(p => p.activo).length;

    document.getElementById('statTotal').textContent = total;
    document.getElementById('statMorales').textContent = morales;
    document.getElementById('statFisicas').textContent = fisicas;
    document.getElementById('statActivos').textContent = activos;
  },

  renderTabla() {
    const tbody = document.getElementById('personasTableBody');

    if (!this.data.personas || this.data.personas.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="9" class="text-center text-muted">
            No se encontraron registros
          </td>
        </tr>
      `;
      return;
    }

    const moduloKey = this._getModuloKey();
    tbody.innerHTML = this.data.personas.map(persona => {
      const roles = persona.roles ? persona.roles.split(', ').map(rol =>
        `<span class="badge ${personaService.getRolBadgeClass(rol)}">${personaService.formatRol(rol)}</span>`
      ).join(' ') : '<span class="text-muted">Sin roles</span>';

      const tipoBadge = `<span class="badge ${persona.tipo_persona === 'moral' ? 'badge-primary' : 'badge-info'}">${persona.tipo_persona === 'moral' ? 'Moral' : 'Física'}</span>`;
      const estadoBadge = `<span class="badge ${persona.activo ? 'badge-success' : 'badge-secondary'}">${persona.activo ? 'Activo' : 'Inactivo'}</span>`;
      const accionesHtml = `
        ${permisos.canView(moduloKey) ? `<button class="btn-icon btn-edit" onclick="personasView.verDetalle(${persona.id})" title="Ver detalle">👁️</button>` : ''}
        ${permisos.canEdit(moduloKey) ? `<button class="btn-icon btn-warning" onclick="personasView.editarPersona(${persona.id})" title="Editar">✏️</button>` : ''}
        ${permisos.canDelete(moduloKey) ? `<button class="btn-icon btn-danger" onclick="personasView.eliminarPersona(${persona.id}, '${persona.nombre_completo.replace(/'/g, "\\'")}')" title="Eliminar">🗑️</button>` : ''}
      `;

      return `
        <tr>
          <td><button class="tbl-expand-btn" onclick="personasView.toggleRow(${persona.id})">&#9654;</button></td>
          <td class="tbl-hide-mobile"><strong>${persona.codigo || '-'}</strong></td>
          <td>
            <div class="user-info">
              <div class="user-name">${persona.nombre_completo}</div>
              ${persona.nombre_comercial ? `<div class="user-username text-muted">${persona.nombre_comercial}</div>` : ''}
            </div>
          </td>
          <td class="tbl-hide-mobile">${persona.rfc || '-'}</td>
          <td class="tbl-hide-mobile">${tipoBadge}</td>
          <td class="tbl-hide-mobile">${roles}</td>
          <td class="tbl-hide-mobile">
            <div style="font-size: 0.85rem;">
              ${persona.email ? `<div>${persona.email}</div>` : ''}
              ${persona.telefono ? `<div class="text-muted">${persona.telefono}</div>` : ''}
            </div>
          </td>
          <td class="tbl-hide-mobile">${estadoBadge}</td>
          <td class="tbl-hide-mobile">
            <div class="action-buttons">${accionesHtml}</div>
          </td>
        </tr>
        <tr class="tbl-detail-row" id="detail-per-${persona.id}">
          <td colspan="9" class="tbl-detail-cell">
            <dl class="tbl-detail-grid">
              <dt>Código</dt><dd>${persona.codigo || '-'}</dd>
              <dt>RFC</dt><dd>${persona.rfc || '-'}</dd>
              <dt>Tipo</dt><dd>${tipoBadge}</dd>
              <dt>Roles</dt><dd>${roles}</dd>
              <dt>Contacto</dt><dd>${persona.email || '-'}${persona.telefono ? ' / ' + persona.telefono : ''}</dd>
              <dt>Estado</dt><dd>${estadoBadge}</dd>
            </dl>
            <div style="margin-top: var(--spacing-sm); display: flex; gap: var(--spacing-xs);">${accionesHtml}</div>
          </td>
        </tr>
      `;
    }).join('');
  },

  toggleRow(id) {
    const detailRow = document.getElementById(`detail-per-${id}`);
    const btn = detailRow?.previousElementSibling?.querySelector('.tbl-expand-btn');
    if (detailRow) {
      detailRow.classList.toggle('open');
      btn?.classList.toggle('open');
    }
  },

  renderPaginacion() {
    const container = document.getElementById('paginationContainer');
    const { page, totalPages, total } = this.data.pagination;

    if (totalPages <= 1) {
      container.innerHTML = total > 0 ? `
        <div class="pagination-info">
          Mostrando ${this.data.personas.length} de ${total} registros
        </div>
      ` : '';
      return;
    }

    let pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }

    container.innerHTML = `
      <div class="pagination">
        <button
          class="pagination-btn"
          ${page === 1 ? 'disabled' : ''}
          onclick="personasView.cambiarPagina(${page - 1})"
        >
          Anterior
        </button>
        <div class="pagination-pages">
          ${pages.map(p => p === '...'
            ? '<span class="pagination-ellipsis">...</span>'
            : `<button
                class="pagination-page ${p === page ? 'active' : ''}"
                onclick="personasView.cambiarPagina(${p})"
              >${p}</button>`
          ).join('')}
        </div>
        <button
          class="pagination-btn"
          ${page === totalPages ? 'disabled' : ''}
          onclick="personasView.cambiarPagina(${page + 1})"
        >
          Siguiente
        </button>
      </div>
      <div class="pagination-info">
        Mostrando ${this.data.personas.length} de ${total} registros
      </div>
    `;
  },

  cambiarPagina(page) {
    this.data.pagination.page = page;
    this.cargarPersonas();
  },

  limpiarFiltros() {
    // Mantener el rol filtrado si viene de URL
    this.data.filters = {
      search: '',
      rol: this.data.rolFiltrado || '',
      tipo_persona: '',
      activo: ''
    };
    this.data.pagination.page = 1;

    document.getElementById('searchInput').value = '';
    if (!this.data.rolFiltrado) {
      document.getElementById('rolFilter').value = '';
    }
    document.getElementById('tipoPersonaFilter').value = '';
    document.getElementById('activoFilter').value = '';

    this.cargarPersonas();
  },

  verDetalle(id) {
    const rolParam = this.data.rolFiltrado ? `?rol=${this.data.rolFiltrado}` : '';
    router.navigate(`/catalogos/personas/${id}${rolParam}`);
  },

  editarPersona(id) {
    const rolParam = this.data.rolFiltrado ? `?rol=${this.data.rolFiltrado}` : '';
    router.navigate(`/catalogos/personas/${id}/editar${rolParam}`);
  },

  async eliminarPersona(id, nombre) {
    const rol = this.data.rolFiltrado;
    // Etiqueta del rol para los textos del modal/toast
    const labelRol = ({
      cliente: 'cliente',
      proveedor: 'proveedor',
      distribuidor: 'distribuidor',
      agente_aduanal: 'agente aduanal',
      responsable: 'responsable'
    })[rol] || 'persona';

    const titulo = rol ? `Eliminar ${labelRol}` : 'Confirmar eliminación';
    const mensaje = rol
      ? `¿Está seguro de eliminar a <strong>${nombre}</strong> como ${labelRol}? La persona se mantendrá registrada con sus otros roles.`
      : `¿Está seguro de eliminar a <strong>${nombre}</strong>? Esta acción no se puede deshacer.`;

    modal.confirm(
      titulo,
      mensaje,
      async () => {
        try {
          if (rol) {
            // Solo desactivar el rol específico, no soft-delete persona
            await personaService.desactivarRolPorPersona(id, rol);
            window.toast.success(`${labelRol.charAt(0).toUpperCase() + labelRol.slice(1)} eliminado`);
          } else {
            // Vista general: soft-delete persona completa
            await personaService.delete(id);
            window.toast.success('Persona eliminada exitosamente');
          }
          await this.cargarPersonas();
        } catch (error) {
          window.toast.error(error.message || 'Error al eliminar');
        }
      }
    );
  }
};
